import { z } from "zod";
import {
  ADMIN_PERMISSIONS,
  ADMIN_ROLES,
  BROADCAST_CHANNELS,
  BROADCAST_TARGETS,
  INBOX_SOURCES,
} from "../domain/admin";

// ---------------------------------------------------------------------------
// Auth — Telegram Login Widget
// ---------------------------------------------------------------------------

/**
 * The payload the Telegram Login Widget POSTs to the panel. The bot-token HMAC
 * is computed over ALL received fields except `hash`, so the schema passes
 * unknown fields through for the verifier (see `@bing/shared/server`).
 */
export const telegramLoginSchema = z
  .object({
    id: z.number().int(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    username: z.string().optional(),
    photo_url: z.string().optional(),
    auth_date: z.number().int(),
    hash: z.string().min(1),
  })
  .passthrough();
export type TelegramLoginInput = z.infer<typeof telegramLoginSchema>;

// ---------------------------------------------------------------------------
// Admin management
// ---------------------------------------------------------------------------

/** Grant or change an admin (keyed by Telegram id — the login identity). */
export const upsertAdminSchema = z.object({
  telegramId: z.string().regex(/^\d+$/, "Must be a numeric Telegram id"),
  role: z.enum(ADMIN_ROLES),
  /** Optional per-admin override grants on top of the role defaults. */
  permissions: z.array(z.enum(ADMIN_PERMISSIONS)).optional(),
});
export type UpsertAdminInput = z.infer<typeof upsertAdminSchema>;

// ---------------------------------------------------------------------------
// Required channels ("join-to-play" gate)
// ---------------------------------------------------------------------------

/** Create or update a required "join-to-play" channel. */
export const upsertRequiredChannelSchema = z.object({
  telegramChatId: z.string().regex(/^-?\d+$/, "Must be a numeric Telegram chat id"),
  title: z.string().trim().min(1).max(128),
  username: z.string().trim().max(64).optional(),
  inviteLink: z.string().url().optional(),
  enabled: z.boolean().default(true),
  order: z.number().int().min(0).default(0),
  /** Feature keys the gate applies to (empty ⇒ gate everything). */
  gatedFeatures: z.array(z.string()).default([]),
});
export type UpsertRequiredChannelInput = z.infer<typeof upsertRequiredChannelSchema>;

// ---------------------------------------------------------------------------
// Broadcasts
// ---------------------------------------------------------------------------

/** Optional inline buttons attached to a broadcast / inbox message. */
export const messageButtonsSchema = z
  .array(z.object({ text: z.string().trim().min(1).max(64), url: z.string().url() }))
  .max(8);

/**
 * Audience filter for `target = SEGMENT`. Every field is optional and AND-combined; an empty
 * object matches everyone (same as ALL). Kept small + declarative so both the admin panel and
 * the recipient resolver (`@bing/db`) agree on the shape. Dates are ISO strings.
 */
export const broadcastSegmentSchema = z
  .object({
    /** Telegram language codes to include (matches any). */
    languageCodes: z.array(z.string().trim().min(2).max(8)).max(20).optional(),
    /** Engagement: games-played bounds (e.g. maxGamesPlayed: 0 → never played). */
    minGamesPlayed: z.number().int().min(0).optional(),
    maxGamesPlayed: z.number().int().min(0).optional(),
    /** Tenure: account creation window. */
    joinedAfter: z.string().datetime().optional(),
    joinedBefore: z.string().datetime().optional(),
    /** Activity: last-seen window (activeAfter → active since; activeBefore → dormant). */
    activeAfter: z.string().datetime().optional(),
    activeBefore: z.string().datetime().optional(),
  })
  .strict();
export type BroadcastSegment = z.infer<typeof broadcastSegmentSchema>;

/** Compose a broadcast. Delivery is throttled to respect Telegram's limits. */
export const createBroadcastSchema = z.object({
  channel: z.enum(BROADCAST_CHANNELS).default("TELEGRAM_PV"),
  title: z.string().trim().max(256).optional(),
  body: z.string().trim().min(1).max(4096),
  /** A Telegram `file_id` (from the bot media-ingest reply) or a public image URL. */
  imageRef: z.string().optional(),
  buttons: messageButtonsSchema.optional(),
  parseMode: z.enum(["HTML", "MarkdownV2"]).optional(),
  target: z.enum(BROADCAST_TARGETS).default("ALL"),
  /** Segment filter when target = SEGMENT. */
  segment: broadcastSegmentSchema.optional(),
  /** Single recipient (User id) when target = SINGLE. */
  targetUserId: z.string().optional(),
  scheduleAt: z.string().datetime().optional(),
});
export type CreateBroadcastInput = z.infer<typeof createBroadcastSchema>;

// ---------------------------------------------------------------------------
// Moderation
// ---------------------------------------------------------------------------

/** Ban a user, optionally with an expiry (omit for a permanent ban). */
export const banUserSchema = z.object({
  userId: z.string().min(1),
  reason: z.string().trim().max(500).optional(),
  expiresAt: z.string().datetime().optional(),
});
export type BanUserInput = z.infer<typeof banUserSchema>;

// ---------------------------------------------------------------------------
// App settings (the singleton) — maintenance, caps, and the three Telegram
// integration groups (support / moderation / error-log). Edited from the panel
// or the in-bot admin flow; the bot reads it live (env is the bootstrap fallback).
// ---------------------------------------------------------------------------

export const ERROR_LOG_SEVERITIES = ["INFO", "WARN", "ERROR", "FATAL"] as const;
export type ErrorLogSeverity = (typeof ERROR_LOG_SEVERITIES)[number];

/** A nullable numeric Telegram chat id (empty/whitespace → null to CLEAR). */
const chatIdField = z
  .string()
  .trim()
  .regex(/^-?\d+$/, "Must be a numeric Telegram chat id")
  .nullable()
  .optional();

/** Update global app settings (partial — only the provided fields change). */
export const updateAppSettingsSchema = z.object({
  maintenanceMode: z.boolean().optional(),
  maintenanceMessage: z.string().max(1000).nullable().optional(),
  concurrentGameCap: z.number().int().min(1).max(100).optional(),
  abandonedGameTtlDays: z.number().int().min(1).max(365).optional(),
  slowModeAvailable: z.boolean().optional(),
  supportGroupChatId: chatIdField,
  moderationGroupChatId: chatIdField,
  errorLogGroupChatId: chatIdField,
  errorLogEnabled: z.boolean().optional(),
  errorLogMinSeverity: z.enum(ERROR_LOG_SEVERITIES).optional(),
});
export type UpdateAppSettingsInput = z.infer<typeof updateAppSettingsSchema>;

/** The full app-settings view returned to the panel (group ids are numeric strings or null). */
export const appSettingsViewSchema = z.object({
  maintenanceMode: z.boolean(),
  maintenanceMessage: z.string().nullable(),
  concurrentGameCap: z.number().int(),
  abandonedGameTtlDays: z.number().int(),
  slowModeAvailable: z.boolean(),
  supportGroupChatId: z.string().nullable(),
  moderationGroupChatId: z.string().nullable(),
  errorLogGroupChatId: z.string().nullable(),
  errorLogEnabled: z.boolean(),
  errorLogMinSeverity: z.enum(ERROR_LOG_SEVERITIES),
});
export type AppSettingsView = z.infer<typeof appSettingsViewSchema>;

/** Toggle a game's availability / maintenance. */
export const updateGameConfigSchema = z.object({
  game: z.enum(["HOKM", "SHELEM", "PASUR", "HEARTS", "BLUFF"]),
  enabled: z.boolean().optional(),
  maintenance: z.boolean().optional(),
});
export type UpdateGameConfigInput = z.infer<typeof updateGameConfigSchema>;

/** Send a system message to a user's in-app inbox. */
export const sendInboxSchema = z.object({
  userId: z.string().min(1),
  source: z.enum(INBOX_SOURCES).default("SYSTEM"),
  title: z.string().trim().max(256).optional(),
  text: z.string().trim().min(1).max(4096),
  imageRef: z.string().optional(),
  buttons: messageButtonsSchema.optional(),
});
export type SendInboxInput = z.infer<typeof sendInboxSchema>;

/** Post an agent reply on a support ticket. */
export const replySupportTicketSchema = z.object({
  ticketId: z.string().min(1),
  text: z.string().trim().min(1).max(4096),
});
export type ReplySupportTicketInput = z.infer<typeof replySupportTicketSchema>;

/** Assign (or unassign) a support ticket to an admin. */
export const assignSupportTicketSchema = z.object({
  ticketId: z.string().min(1),
  adminId: z.string().min(1).nullable(),
});
export type AssignSupportTicketInput = z.infer<typeof assignSupportTicketSchema>;

/** Resolve/close a support ticket. */
export const closeSupportTicketSchema = z.object({
  ticketId: z.string().min(1),
});
export type CloseSupportTicketInput = z.infer<typeof closeSupportTicketSchema>;

/** Create or update a canned support reply. */
export const upsertCannedReplySchema = z.object({
  shortcut: z.string().trim().min(1).max(64),
  title: z.string().trim().min(1).max(128),
  body: z.string().trim().min(1).max(4096),
});
export type UpsertCannedReplyInput = z.infer<typeof upsertCannedReplySchema>;
